import type { ReactNode } from "react";
import { cn } from "./utils/cn";
import { Tooltip } from "./Tooltip";

export type SidebarItem = {
  id: string;
  label: string;
  icon?: ReactNode;
  active?: boolean;
  href?: string;
  onClick?: (e: React.MouseEvent) => void;
  badge?: ReactNode | number;
};

type SidebarGroup = { label?: string; items: SidebarItem[] };

export function Sidebar({
  header,
  items,
  groups,
  footer,
  collapsed = false,
  onToggle,
  onNavigate,
  collapsedWidth,
  expandedWidth,
}: {
  header?: ReactNode;
  items?: SidebarItem[];
  groups?: SidebarGroup[];
  footer?: ReactNode;
  collapsed?: boolean;
  onToggle?: () => void;
  onNavigate?: () => void; // called after a navigation action (useful to close on mobile)
  collapsedWidth?: string; // tailwind width class
  expandedWidth?: string; // tailwind width class
}) {
  const collapsedW = collapsedWidth || "w-14";
  const expandedW = expandedWidth || "w-60";
  return (
    <aside
      className={cn(
        // sticky sidebar with its own scroll; stays visible, unless content overflows inside
        "sticky top-0 z-20 flex max-h-screen flex-col overflow-auto border-r bg-surface transition-all duration-300 ease-in-out motion-reduce:transition-none",
        collapsed ? collapsedW : expandedW,
      )}
      aria-label="Navigation latérale"
    >
      <div className="flex items-center justify-between border-b p-3">
        <div className={cn("text-sm font-semibold", collapsed && "sr-only")}>{header}</div>
        {onToggle && (
          <button
            type="button"
            aria-label={collapsed ? "Déplier le menu" : "Replier le menu"}
            className="rounded p-1 text-muted hover:bg-muted/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            onClick={onToggle}
            title={collapsed ? "Déplier" : "Replier"}
          >
            <svg
              className="h-4 w-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M9 6l-6 6 6 6" />
              <path d="M3 12h18" />
            </svg>
          </button>
        )}
      </div>
      <nav className="flex-1 space-y-3 p-2">
        {(groups && groups.length > 0 ? groups : [{ items: items || [] }]).map((g, gi) => (
          <div key={gi} className="space-y-1">
            {g.label && !collapsed && (
              <div
                className={cn(
                  "px-3 text-[11px] font-semibold uppercase tracking-wide",
                  g.items.some((it) => it.active) ? "text-accent" : "text-muted",
                )}
              >
                {g.label}
              </div>
            )}
            {g.items.map((it) => {
              const baseLink = (
                <a
                  key={it.id}
                  href={it.href || "#"}
                  onClick={(e) => {
                    it.onClick?.(e);
                    // auto-close on mobile after navigation
                    if (typeof window !== "undefined" && window.innerWidth < 768) onNavigate?.();
                  }}
                  className={cn(
                    "group relative flex items-center rounded-md py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent transition-colors",
                    collapsed ? "justify-center px-2 gap-0" : "gap-3 px-3",
                    it.active
                      ? "bg-accent/10 text-text"
                      : "text-muted hover:bg-muted/10 hover:text-text",
                  )}
                >
                  <span
                    className={cn(
                      "grid h-5 w-5 place-items-center transition-transform duration-200 motion-reduce:transition-none",
                      it.active ? "text-accent" : "text-muted",
                      !collapsed && !it.active && "group-hover:translate-x-0.5",
                    )}
                  >
                    {it.icon}
                  </span>
                  <span className={cn("truncate", collapsed && "sr-only")}>{it.label}</span>
                  {!collapsed && it.badge !== undefined && (
                    <span className="ml-auto inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-muted/20 px-1.5 text-[11px] text-text">
                      {it.badge}
                    </span>
                  )}
                  {!collapsed && (
                    <span
                      className={cn(
                        "pointer-events-none absolute left-0 top-0 h-full w-1 rounded-r bg-accent transition-opacity",
                        it.active ? "opacity-100" : "opacity-0 group-hover:opacity-60",
                      )}
                    />
                  )}
                </a>
              );
              return collapsed ? (
                <Tooltip key={it.id} label={String(it.label)} placement="right" block>
                  {baseLink}
                </Tooltip>
              ) : (
                baseLink
              );
            })}
            {gi < (groups?.length || 1) - 1 && <div className="mx-2 border-t" />}
          </div>
        ))}
      </nav>
      {footer && <div className={cn("border-t p-3", collapsed && "sr-only")}>{footer}</div>}
    </aside>
  );
}
